import "../css/header.scss";
import React, { useContext, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FiChevronDown, FiMenu, FiSearch, FiShoppingBag, FiX } from "react-icons/fi";
import { CartContext } from "./CartContext";
import siteConfig from "../siteConfig";
import BrandMark from "./BrandMark";
import { policyDocuments } from "../policies";

const menus = [
  { label: "Sale", category: "Sale" },
  {
    label: "Decorative Homeware",
    items: ["Art + Prints", "Baskets + Storage", "Cushions + Throws", "Candles + Holders", "Pots + Vases", "Rugs + Textiles"],
  },
  {
    label: "Kitchen + Dining + Utility Homeware",
    items: ["Ceramics", "Kitchen + Dining", "Pantry", "Bath + Body"],
  },
  { label: "Lifestyle", items: ["Books + Events", "Bath + Body", "Baskets + Storage", "Pantry"] },
];

function Header() {
  const { cartItems } = useContext(CartContext);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [query, setQuery] = useState("");
  const moreRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const count = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
    setMoreOpen(false);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [location.pathname, location.search]);

  useEffect(() => {
    const close = (event) => {
      if (moreRef.current && !moreRef.current.contains(event.target)) setMoreOpen(false);
    };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, []);

  if (["/checkout", "/payment", "/paysuccess"].includes(location.pathname)) return null;

  const submitSearch = (event) => {
    event.preventDefault();
    const next = query.trim();
    if (next) navigate(`/shop?search=${encodeURIComponent(next)}`);
  };

  return (
    <>
      <div className="announcement">{siteConfig.announcement}</div>
      <header className="site-header">
        <button className="header-icon mobile-only" aria-label="Open menu" onClick={() => setMenuOpen(true)}><FiMenu /></button>
        <div className="header-context desktop-only"><span>{siteConfig.operatingRegion}</span><span>{siteConfig.currency}</span></div>
        <Link to="/" className="brand-mark" aria-label={`${siteConfig.brandName} home`}><BrandMark /></Link>
        <div className="header-actions">
          <button className="header-icon" aria-label="Search" onClick={() => setSearchOpen(true)}><FiSearch /></button>
          <Link className="header-icon cart-link" to="/cartpage" aria-label={`Cart with ${count} items`}><FiShoppingBag />{count > 0 && <span>{count}</span>}</Link>
        </div>
        <nav className="desktop-nav" aria-label="Primary navigation">
          {menus.map((menu) => (
            <div className="nav-group" key={menu.label}>
              <Link to={`/shop?category=${encodeURIComponent(menu.category || menu.label)}`}>{menu.label}</Link>
              {menu.items && <div className="mega-menu">{menu.items.map((item) => <Link key={item} to={`/shop?category=${encodeURIComponent(item)}`}>{item}</Link>)}</div>}
            </div>
          ))}
          <Link to="/about">Our Story</Link>
          <div className={`nav-group more-group ${moreOpen ? "is-open" : ""}`} ref={moreRef}>
            <button className="more-trigger" type="button" aria-expanded={moreOpen} aria-controls="more-menu" onClick={() => setMoreOpen((open) => !open)}>More <FiChevronDown /></button>
            <div className="mega-menu policy-menu" id="more-menu">
              {policyDocuments.map((policy) => <Link key={policy.path} to={policy.path}>{policy.shortTitle}</Link>)}
            </div>
          </div>
        </nav>
      </header>

      <div className={`drawer ${menuOpen ? "is-open" : ""}`} aria-hidden={!menuOpen}>
        <button className="drawer-close" onClick={() => setMenuOpen(false)} aria-label="Close menu"><FiX /></button>
        <Link to="/" className="drawer-logo"><BrandMark compact /></Link>
        {menus.map((menu) => <div key={menu.label} className="drawer-group"><Link to={`/shop?category=${encodeURIComponent(menu.category || menu.label)}`}>{menu.label}</Link>{menu.items?.map((item) => <Link className="drawer-sub" key={item} to={`/shop?category=${encodeURIComponent(item)}`}>{item}</Link>)}</div>)}
        <div className="drawer-group"><Link to="/about">Our Story</Link><Link className="drawer-sub" to="/contact">Contact</Link></div>
        <div className="drawer-group"><span className="drawer-heading">More</span>{policyDocuments.map((policy) => <Link className="drawer-sub" key={policy.path} to={policy.path}>{policy.shortTitle}</Link>)}</div>
      </div>
      {menuOpen && <button className="drawer-backdrop" aria-label="Close menu" onClick={() => setMenuOpen(false)} />}

      <div className={`search-panel ${searchOpen ? "is-open" : ""}`} aria-hidden={!searchOpen}>
        <button onClick={() => setSearchOpen(false)} aria-label="Close search"><FiX /></button>
        <form onSubmit={submitSearch}><label htmlFor="site-search">Search our collection</label><div><input id="site-search" autoFocus={searchOpen} value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search by name or material" /><button type="submit" aria-label="Submit search"><FiSearch /></button></div></form>
      </div>
    </>
  );
}

export default Header;
